import {
  BuildContainerObject,
  ParseCartItemForContainer,
  formatCollectionName,
  CurrencyConverter,
} from "./container-utils.js";

class CartContainerOrderSummary extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const mainContentDiv = this.querySelector("#main");
    this.mainContentDiv = mainContentDiv;

    const lineItemsHTML = this.querySelector("#items")?.innerHTML;

    let cartData = {};
    let lineItems = [];

    try {
      cartData = JSON.parse(lineItemsHTML);
      lineItems = cartData.lineItems;
    } catch (e) {
      console.error("Error when parsing data from cart: ", e);
    }

    this.currency = CurrencyConverter(cartData.currencyCode ?? "");
    this.isLoggedIn = cartData.isLoggedIn === "true" ? true : false;
    this.lineItems = lineItems;

    const containerObject = BuildContainerObject(
      this.currency,
      this.lineItems,
      ParseCartItemForContainer,
    );

    this.containerObject = containerObject ?? {};

    this.buildCollectionContainers();
  }

  buildCollectionContainers() {
    Object.entries(this.containerObject)?.map(
      ([collectionName, collectionContainerObject], _) => {
        const formattedCollectionName = formatCollectionName(collectionName);

        this.mainContentDiv.insertAdjacentElement(
          "beforeend",
          new SingleCollectionContainer(
            formattedCollectionName,
            collectionContainerObject,
            this.isLoggedIn,
          ).create(),
        );
      },
    );
  }
}

class SingleCollectionContainer {
  constructor(collectionName, collectionContainerObject, isLoggedIn) {
    this.collectionContainerObject = collectionContainerObject;
    this.collectionName = collectionName;
    this.isLoggedIn = isLoggedIn;

    this.singleContainerObject = document.createElement("div");
    this.singleContainerObject.classList.add("single-container");
  }

  create() {
    this.createSingleContainerHeader();
    this.createSingleContainerBody();

    // insert content
    this.insertWeightDistributionIntoContainerBody();
    this.insertSummaryIntoContainerBody();

    return this.singleContainerObject;
  }

  createSingleContainerHeader() {
    this.singleContainerObject.insertAdjacentHTML(
      "afterbegin",
      `<div class="single-container__header">
            <h3 class="collection-name">
              Collection: ${this.collectionName}
            </h3>
        </div>`,
    );
  }

  createSingleContainerBody() {
    const singleContainerBody = document.createElement("div");
    singleContainerBody.classList.add("single-container__body");

    this.singleContainerObject.insertAdjacentElement(
      "beforeend",
      singleContainerBody,
    );
    this.singleContainerBody = singleContainerBody;
  }

  insertWeightDistributionIntoContainerBody() {
    // create and insert outward container
    const weightDistributionContainer = document.createElement("div");
    weightDistributionContainer.classList.add(
      "single-container__body__container-info",
      "weight-distribution",
    );
    this.singleContainerBody.insertAdjacentElement(
      "afterbegin",
      weightDistributionContainer,
    );

    // create and insert body element of container
    const weightDistribContainerBody = document.createElement("div");
    weightDistribContainerBody.classList.add("body");
    weightDistributionContainer.insertAdjacentElement(
      "afterbegin",
      weightDistribContainerBody,
    );

    const containerList = this.collectionContainerObject.containerList;

    containerList?.map((singleContainer, index) => {
      const percentage =
        (singleContainer.totalPallets /
          this.collectionContainerObject.maxPallet) *
        100;

      weightDistribContainerBody.insertAdjacentHTML(
        "afterbegin",
        `
        <div class="container-distribution-progress-bar-wrapper">
          <div class="container-title">&#8226; Container #K100${index + 1}</div>
          <div class="progress-bar">
            <div
              class="inner-bar"
              style="width: ${percentage}%;"
            >
              <small
                class="percentage-info ${index < containerList.length - 1 ? "full" : ""}"
              >
                ${index < containerList.length - 1 ? "Estimated: FULL" : `Estimated: ${percentage.toFixed(2)}%`}
              </small>
            </div>
          </div>
        </div>
        `,
      );
    });
  }

  insertSummaryIntoContainerBody() {
    const summaryContainer = document.createElement("div");
    summaryContainer.classList.add("single-container__body__container-info");
    summaryContainer.classList.add("summary");

    this.singleContainerBody.insertAdjacentElement(
      "beforeend",
      summaryContainer,
    );

    const summarryContainerBody = document.createElement("div");
    summarryContainerBody.classList.add("body");
    summaryContainer.insertAdjacentElement("afterbegin", summarryContainerBody);

    // insert single info display
    const collectionWeightDisplay = new SingleInfoDisplay(
      `Total collection weight: <span class="important">${this.collectionContainerObject.totalWeight} (lbs)</span> <span class="extra"> / ${this.collectionContainerObject.maxWeight} lbs / Container`,
    ).create();
    summarryContainerBody.insertAdjacentElement(
      "afterbegin",
      collectionWeightDisplay,
    );

    if (this.isLoggedIn) {
      const collectionCostDisplay = new SingleInfoDisplay(
        `Total collection cost: <span class="important">${this.collectionContainerObject.currencyCode}${this.collectionContainerObject.totalCost.toFixed(2)}</span>`,
      ).create();
      summarryContainerBody.insertAdjacentElement(
        "afterbegin",
        collectionCostDisplay,
      );
    }

    const numberOfPalletsDisplay = new SingleInfoDisplay(
      `No. of pallets: <span class="important">${this.collectionContainerObject.totalPallets.toFixed(2)} pallets</span>`,
    ).create();
    summarryContainerBody.insertAdjacentElement(
      "afterbegin",
      numberOfPalletsDisplay,
    );

    const numberOfSetsDisplay = new SingleInfoDisplay(
      `No. of sets: <span class="important">${this.collectionContainerObject.totalSets} sets</span>`,
    ).create();
    summarryContainerBody.insertAdjacentElement(
      "afterbegin",
      numberOfSetsDisplay,
    );

    const numberOfColorsDisplay = new SingleInfoDisplay(
      `No. of colors: <span class="important">${this.collectionContainerObject.totalColor}</span>`,
    ).create();
    summarryContainerBody.insertAdjacentElement(
      "afterbegin",
      numberOfColorsDisplay,
    );

    const numberOfStyles = new SingleInfoDisplay(
      `No. of styles: <span class="important">${this.collectionContainerObject.totalStyle}</span> <span class="extra"> / Max: ${this.collectionContainerObject.maxStyle} </span>`,
    ).create();
    summarryContainerBody.insertAdjacentElement("afterbegin", numberOfStyles);
  }
}

class SingleInfoDisplay {
  constructor(contentString) {
    this.contentString = contentString;
    this.displayElement = document.createElement("div");
    this.displayElement.classList.add("single-info-display");
  }

  create() {
    this.insertContentToDisplay();

    return this.displayElement;
  }

  insertContentToDisplay() {
    if (this.contentString) {
      const arrowElement = document.createElement("div");
      arrowElement.classList.add("icon");
      arrowElement.innerHTML = "&#8226;";

      const contentElement = document.createElement("div");
      contentElement.classList.add("info");
      contentElement.innerHTML = this.contentString;

      this.displayElement.insertAdjacentElement("afterbegin", arrowElement);
      this.displayElement.insertAdjacentElement("beforeend", contentElement);
    }
  }
}

// define custom cart summary element
customElements.define(
  "cart-container-order-summary",
  CartContainerOrderSummary,
);
